<template>
    <v-card>
        <div style="width: 100%; display: flex; justify-content: space-between;">
            <h3>Size Mappings</h3>
            <v-btn variant="outlined" @click="create">Add new size mapping</v-btn>
        </div>
        <v-data-table :headers="headers" :items="props.sizeMapperData.mappings">
            <!-- Categories cell -->
            <template v-slot:item.categories="{ value }">
                {{ (value || []).length }} {{ (value || []).length === 1 ? "Category" : "Categories" }}
            </template>
            <!-- Mappings cell -->
            <template v-slot:item.mappings="{ value }">
                {{ (value || []).length }} {{ (value || []).length === 1 ? "Mapping" : "Mappings" }}
            </template>
            <!-- Values cell -->
            <template v-slot:item.values="{ value }">
                {{ (value || []).length }} {{ (value || []).length === 1 ? "Value" : "Values" }}
            </template>
            <!-- Brands cell -->
            <template v-slot:item.brands="{ value }">
                <span v-if="(value || []).length === 0">All brands</span>
                <span v-if="(value || []).length === 1">1 Brand</span>
                <span v-if="(value || []).length > 1">{{ value.length }} Brands</span>
            </template>
            <!-- Actions cell -->
            <template v-slot:item.actions="{ item }">
                <div class="d-flex ga-2 justify-end">
                    <v-icon color="medium-emphasis" icon="mdi-pencil" size="small" @click="edit(item.name)"></v-icon>
                    <v-icon color="medium-emphasis" icon="mdi-delete" size="small" @click="remove(item.name)"></v-icon>
                </div>
            </template>
        </v-data-table>
    </v-card>

    <v-dialog v-model="dialog" max-width="1000">
        <v-card :title="`Editing size mapping`">
            <template v-slot:text>
                <!-- Name -->
                <v-text-field v-model="formModel.name" label="Name"></v-text-field>

                <!-- Categories -->
                <v-autocomplete chips label="Affected Categories" v-model="formModel.categories"
                    :items="backendCategories" multiple></v-autocomplete>

                <!-- Attributes -->
                <v-autocomplete chips label="Affected Attributes" v-model="formModelAttributes"
                    :items="attributesSimplified" multiple></v-autocomplete>

                <!-- Attributes -->
                <v-autocomplete chips label="Affected Brands" placeholder="All Brands" persistent-placeholder v-model="formModel.brands"
                    :items="brandsListMapped" multiple @update:search="(e) => emit('searchBrands', e)" no-data-text="Type to search brands">
                </v-autocomplete>

                <!-- Mappings -->
                <h3>Mappings:</h3>
                <div v-for="mapping in formModel.mappings" style="margin: 20px">
                    <v-icon color="medium-emphasis" icon="mdi-delete" size="small"
                        @click="removeMapping(mapping)"></v-icon>
                    <v-chip size="small" v-for="name in getDisplayValueForAttributeIdList(mapping.from)">
                        {{ name }}
                    </v-chip>
                    → <v-chip size="small">{{ getDisplayValueForAttributeId(mapping.to) }}</v-chip>
                </div>

                <v-row>
                    <v-col cols="4">
                        <v-autocomplete density="compact" chips label="From" v-model="newFrom"
                            :items="selectedAttributesSimplified" multiple></v-autocomplete>
                    </v-col>
                    <v-col cols="4">
                        <v-autocomplete density="compact" chips label="To" v-model="newTo"
                            :items="selectedAttributesSimplified"></v-autocomplete>
                    </v-col>
                    <v-col cols="2">
                        <v-btn @click="addMapping">Add Mapping</v-btn>
                    </v-col>
                </v-row>

                <!-- Values table -->
                <div style="width: 100%; display: flex; justify-content: space-between;">
                    <h3>Values Table:</h3>
                    <v-btn variant="outlined" @click="addValue">Add value to table</v-btn>
                </div>
                <v-data-table :headers="[
                    ...formModelAttributes?.map(atr => ({ key: atr, title: getDisplayValueForAttributeId(atr), sortable: false })),
                    { title: 'Actions', key: 'actions', align: 'end', sortable: false }
                ]" :items="formModel.values">
                    <template v-slot:item="{ item }">
                        <tr class="text-no-wrap">
                            <td v-for="attr in formModelAttributes">
                                <v-autocomplete v-model:search="currentSearchText" density="compact" v-model="item[attr]"
                                    :items="attributeValueDropdownItems[attr]"></v-autocomplete>
                            </td>

                            <td>
                                <div class="d-flex ga-2 justify-end">
                                    <v-icon color="medium-emphasis" icon="mdi-delete"
                                        @click="removeValue(item)"></v-icon>
                                </div>
                            </td>
                        </tr>
                    </template>
                </v-data-table>
            </template>

            <v-divider></v-divider>

            <v-card-actions class="bg-surface-light">
                <v-btn text="Cancel" variant="plain" @click="dialog = false"></v-btn>

                <v-spacer></v-spacer>

                <v-btn text="Save" @click="save"></v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>
<script setup>
import { computed, ref, shallowRef, unref, watch } from 'vue'
const emit = defineEmits(['dataChanged', 'searchBrands']);
const props = defineProps({
    attributeData: Object,
    sizeMapperData: Object,
    backendTaxonomyData: Object,
    brandsList: Object,
});

const currentSearchText = ref('');

const backendCategories = computed(() => {
    const collectPaths = (currentPath, allPaths, node) => {
        const newCurrentPath = currentPath === "" ? node.name : `${currentPath} | ${node.name}`;
        const childAllPaths = node.subcategories ? node.subcategories.flatMap(subcat => collectPaths(newCurrentPath, allPaths, subcat)) : [];
        return [{ title: newCurrentPath, value: node.id }, ...childAllPaths];
    }

    const rootNode = props.backendTaxonomyData?.["Root"] || props.backendTaxonomyData?.["Fashion"] || {};
    return collectPaths("", [], rootNode);
});

const attributesSimplified = computed(() => {
    return Object.entries(props.attributeData).map(([id, rest]) => {
        return { value: id, title: `${rest.display_name.en} (${rest.name})` };
    });
});

const attributeValueDropdownItems = computed(() => {
    return Object.entries(props.attributeData).reduce((finalObject, [attributeId, attribute]) => {
        // Override for generic size attribute usage
        if (attributeId === "60001746" && currentSearchText.value.trim() !== "") {
            return {
                ...finalObject,
                "60001746": [{ value: currentSearchText.value, title: currentSearchText.value }],
            };
        }

        return {
            ...finalObject,
            [attributeId]: Object.entries(attribute.values).map(([valueId, { value_en }]) => ({ value: valueId, title: value_en })),
        };
    }, {});
});

const brandsListMapped = computed(() => {
    return props.brandsList?.map((brand) => ({ value: brand.brandId, title: brand.name }));
});

watch(
    () => props.sizeMapperData,
    (data) => {
        for (const brandId of data.mappings.flatMap(mapping => mapping.brands || [])) {
            emit("searchBrands", brandId);
        }
    },
    { immediate: true }
);

const isEditingExisting = ref(false);
const originalName = ref("");
const formModel = ref({});
const formModelAttributes = ref([]);
const newFrom = ref([]);
const newTo = ref(null);
const dialog = shallowRef(false);

const selectedAttributesSimplified = computed(() => {
    return attributesSimplified.value.filter(attr => {
        return formModelAttributes.value.includes(attr.value);
    });
});

const headers = [
    { title: 'Name', key: 'name', align: 'start' },
    { title: 'Categories', key: 'categories' },
    { title: 'Brands', key: 'brands' },
    { title: 'Mappings', key: 'mappings' },
    { title: 'Values', key: 'values' },
    { title: 'Actions', key: 'actions', align: 'end', sortable: false },
]

function getDisplayValueForAttributeIdList(idList) {
    return idList.map(id => getDisplayValueForAttributeId(id));
}

function getDisplayValueForAttributeId(id) {
    return (props.attributeData[id] || { display_name: { en: "???" } }).display_name.en;
}

function save() {
    let newMappingData = {};

    // Strip out any attributes that were added and then removed
    formModel.value.values = formModel.value.values.map((valueObject) => {
        return Object.fromEntries(
            Object.entries(valueObject).filter(([key]) => formModelAttributes.value.includes(key))
        );
    });

    if (isEditingExisting.value) {
        newMappingData = {
            ...props.sizeMapperData,
            mappings: props.sizeMapperData.mappings.map(m => m.name === originalName.value ? unref(formModel) : m)
        }
    } else {
        newMappingData = {
            ...props.sizeMapperData,
            mappings: [...props.sizeMapperData.mappings, unref(formModel)]
        }
    }

    // Name should be unique
    const names = newMappingData.mappings.map(m => m.name);
    if (names.length !== new Set(names).size) { // Trick to remove duplicates. If the length isnt the same, there was a double
        alert(`There is already a rule with the name '${formModel.value.name}', please try something else.`);
    } else {
        emit('dataChanged', newMappingData);
        dialog.value = false;
    }
}

function create() {
    isEditingExisting.value = false;
    formModel.value = { name: "New Mapper", brands: [], categories: [], mappings: [], values: [] };
    formModelAttributes.value = [];
    dialog.value = true;
}

function edit(name) {
    isEditingExisting.value = true;
    originalName.value = name;
    formModel.value = JSON.parse(JSON.stringify(props.sizeMapperData.mappings.find(mapping => mapping.name === name)));
    formModelAttributes.value = Object.keys(Object.assign({}, ...(formModel.value?.values || [])));
    dialog.value = true;
}

function remove(name) {
    emit('dataChanged', {
        ...props.sizeMapperData,
        mappings: props.sizeMapperData.mappings.filter(m => m.name !== name)
    });
}

function addMapping() {
    formModel.value.mappings = [...(formModel.value.mappings || []), {
        from: newFrom.value,
        to: newTo.value,
    }];

    newFrom.value = [];
    newTo.value = null;
}

function removeMapping(mapping) {
    formModel.value.mappings = formModel.value.mappings.filter(m => m !== mapping);
}

function addValue() {
    formModel.value.values = [...formModel.value.values, formModelAttributes.value.reduce((newObj, id) => {
        return { ...newObj, [id]: "" };
    }, {})];
}

function removeValue(value) {
    formModel.value.values = formModel.value.values.filter(v => v !== value);
}
</script>